import React, { useEffect, useState } from 'react';
import useDebounce from 'src/util/hooks/use-debounce';
import Dialog from '../../../../common/Dialog.redesign';
import Checkbox from '../../../../controls/forms/Checkbox';
import TextInput from '../../../../controls/forms/TextInput';
import Button from 'src/components/controls/forms/redesign/FormButton';
import useFetch from 'use-http';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import { Url } from 'src/api';

interface ICloneORRIDialog {
    targetWorkingInterestId: number;
    targetWorkingInterestName: string;
    onComplete: () => void;
    onCloseDialog?: () => void;
}

const CloneORRIDialog: React.FC<ICloneORRIDialog> = (props) => {
    const [replaceORRI, setReplaceORRI] = useState(true);
    const [workingInterest, setWorkingInterest] = useState(undefined);
    const [currentWorkingInterestNumber, setCurrentWorkingInterestNumber] = useState('');
    const debouncedWorkingInterestNumber = useDebounce(currentWorkingInterestNumber, 500);
    const [status, setStatus] = useState({ loading: false, error: false, message: '' });
    const { get, post, response, error } = useFetch();

    const appContext = useSelector((storeState: IRootState) => storeState.appContext);

    const searchWorkingInterestNumber = async (workingInterestNumber: string) => {
        setWorkingInterest(undefined);
        const result = await get(`${Url.project.getProject(appContext, appContext.projectNumber)}\\leaseholdownerships\\${workingInterestNumber}`);

        if (response.ok) {
            setWorkingInterest(result);
            if (!result) {
                setStatus({ loading: false, error: true, message: 'Working Interest Not Found' });
            } else if (result.orriOwnerships.length <= 0) {
                setStatus({ loading: false, error: true, message: 'Working Interest Has No Overriding Royalty Interest' });
            } else {
                setStatus({ loading: false, error: false, message: '' });
            }
        }
    };

    const cloneWorkingInterest = async () => {
        const body = {
            targetWorkingInterestId: props.targetWorkingInterestId,
            sourceWorkingInterestId: parseInt(currentWorkingInterestNumber, 10),
            clearWorkingInterests: replaceORRI,
            leaseholdId: appContext.leaseholdId,
        };

        await post(`${Url.project.getProject(appContext, appContext.projectNumber)}\\leaseholdownerships\\clone-ownership`, body);
        if (props.onCloseDialog) {
            props.onCloseDialog();
        }
        if (props.onComplete) {
            props.onComplete();
        }
    };

    useEffect(() => {
        if (error) {
            console.log(error);
        }
    }, [error]);

    useEffect(() => {
        if (!debouncedWorkingInterestNumber) {
            setWorkingInterest(undefined);
            setStatus({ loading: false, error: false, message: '' });
        } else {
            const numberRegex = /^[0-9]+$/;
            if (!numberRegex.test(debouncedWorkingInterestNumber.toString())) {
                setStatus({ loading: false, error: true, message: 'Invalid Working Interest Number Provided' });
            } else if (debouncedWorkingInterestNumber.toString() === props.targetWorkingInterestId.toString()) {
                setStatus({ loading: false, error: true, message: 'Cannot copy ORRI onto itself' });
            } else {
                setStatus({ loading: true, error: false, message: 'Verifying Working Interest Number' });
                searchWorkingInterestNumber(debouncedWorkingInterestNumber.toString());
            }
        }
    }, [debouncedWorkingInterestNumber]);

    const searchResultsPanel = () => {
        let content = (
            <>
                <div>Fill in Working Interest Number to Search</div>
            </>
        );

        if (status.loading || status.error) {
            content = (
                <>
                    {status.loading && <div>loading...</div>}
                    {status.error && <div style={{ color: 'red' }}>{status.message}</div>}
                </>
            );
        } else if (!workingInterest) {
            content = <div>Enter Working Interest Number.</div>;
        } else {
            content = (
                <div style={{ textAlign: 'left' }}>
                    {`${workingInterest.owner.name} - ${workingInterest.owner.ownerType} · ${workingInterest.orriOwnerships.length} ORRIs`}
                </div>
            );
        }

        return (
            <div style={{ padding: '1rem', textAlign: 'center', background: 'white', border: '1px solid #ccc', fontWeight: 'bold' }}>{content}</div>
        );
    };

    return (
        <Dialog title={`Copy ORRI to ${props.targetWorkingInterestName} `}>
            <div style={{ display: 'flex' }}>
                <TextInput
                    label="Working Interest Number"
                    onChange={(e) => setCurrentWorkingInterestNumber(e.target.value)}
                    style={{ width: '50%' }}
                />
                <Checkbox
                    label="Replace Current ORRI"
                    value={replaceORRI}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReplaceORRI(e.target.checked)}
                    wrapped={true}
                    style={{ width: '50%', marginLeft: '1rem' }}
                />
            </div>
            {searchResultsPanel()}
            <hr />
            <div style={{ textAlign: 'right', margin: '.7rem 0 .7rem 0' }}>
                <Button disabled={!workingInterest} type="submit" text="Copy" intent="primary" onClick={cloneWorkingInterest} />
            </div>
        </Dialog>
    );
};

CloneORRIDialog.displayName = 'CloneORRIDialog';
export default CloneORRIDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/dialogs/CloneORRIDialog.tsx